<script setup lang="ts">
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import { ref, computed, onMounted } from "vue";
import { Carousel, Slide } from "vue3-carousel";
import leftButton from "@/assets/img/icon/left-arrow.svg";
import rightButton from "@/assets/img/icon/right-arrow.svg";
import type { Course } from "~/types/Cours";
import type { Swiper as SwiperClass } from "swiper"; // <-- types

const { getCourses } = useCoursApi();
const position = ref(1);
const swiperRef = shallowRef<SwiperClass | null>(null);
const courses = ref<Course[]>([]);
const current = ref(0);
const itemShow = ref(4);
const isMobile = ref(false);

// safer total count
const total = computed(() => courses.value.length);

const setResponsive = () => {
  const w = window.innerWidth;
  if (w < 640) {
    itemShow.value = 1;
    isMobile.value = true;
  } else if (w < 1024) {
    itemShow.value = 3;
    isMobile.value = false;
  } else {
    itemShow.value = 4;
    isMobile.value = false;
  }
};
const onSwiper = (sw: SwiperClass) => {
  // ✅ define handler
  swiperRef.value = sw;
};

const prev = () => {
  swiperRef.value?.slidePrev();
  if (position.value <= 1) {
    position.value = 1;
  } else {
    position.value = position.value - 1;
  }
};
const next = () => {
  swiperRef.value?.slideNext();
  if (position.value >= total.value) {
    position.value = total.value;
  } else {
    position.value = position.value + 1;
  }
};

onMounted(() => {
  window.addEventListener("resize", setResponsive);
  setResponsive();
  getData();
});
onUnmounted(() => window.removeEventListener("resize", setResponsive));
const endIndex = computed(() =>
  Math.min(total.value - 1, position.value + itemShow.value - 2)
);

const isEnd = (idx: number) => idx === endIndex.value;

const getData = async () => {
  const data = await getCourses();
  if (data?.member) {
    courses.value = data.member;
    if (current.value >= data.member.length) current.value = 0;
  }
};

onMounted(getData);
</script>

<template>
  <section class="px-4 md:px-12 w-[100vw] overflow-x-hidden">
    <div class="max-w-6xl mx-auto text-center mb-12">
      <span
        class="text-sm font-semibold uppercase tracking-wide topButton mx-auto font-poppins text-blue"
      >
        programas formativos
      </span>
      <h2 class="text-xl md:text-4xl font-bold text-gray-900 mt-2 font-poppins">
        Descubra nuestros cursos más destacados
      </h2>
    </div>
    <ClientOnly>
      <Swiper
        :slides-per-view="'auto'"
        :space-between="30"
        :speed="250"
        :grab-cursor="false"
        :allow-touch-move="false"
        :simulate-touch="false"
        :mousewheel="false"
        :keyboard="false"
        :slide-to-clicked-slide="false"
        @swiper="onSwiper"
        class="hover-swiper"
      >
        <SwiperSlide
          v-for="(course, index) in courses"
          :key="course.id"
          :class="['hover-slide', isEnd(index) ? 'end-item' : '']"
          @mouseenter="current = index"
          @mouseleave="current = -1"
        >
          <div class="card">
            <HomeSectionsCourseCard
              :activeItem="isMobile ? false : current === index"
              :item="course"
            />
          </div>
        </SwiperSlide>
      </Swiper>
    </ClientOnly>
    <div class="flex justify-between items-center my-6 gap-6">
      <div class="flex gap-2">
        <span class="font-bold font-poppins text-blue text-[26px]">{{
          position
        }}</span>
        <span class="font-bold font-poppins text-gray text-[26px]"
          >/{{ total }}</span
        >
      </div>
      <div class="w-full h-[1px] bg-gray"></div>
      <div class="flex justify-end gap-4">
        <button
          @click="prev"
          class="rounded-full left bg-[#e7e7e7] hover:shadow-sm hover:bg-[#d5d5d5]"
        >
          <img :src="leftButton" alt="left" />
        </button>
        <button
          @click="next"
          class="right bg-blue hover:bg-[#0d5aff] hover:shadow-sm rounded-full"
        >
          <img :src="rightButton" alt="right" />
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.topButton {
  border-radius: 24px;
  background: var(--Subtle-Blue, #e8f6ff);
  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  width: fit-content;
}
button {
  width: 56px;
  height: 56px;
  transition: all 0.3s ease;
}

/* -------------------- */
/* Swiper base + slots  */
/* -------------------- */
.hover-swiper {
  /* slot width per breakpoint */
  --slot: 23.5%; /* desktop default: 4 per row */
  --gap: 20px; /* matches :space-between="10" */
  padding: 1rem;
  overflow: hidden;
}
.hover-swiper :deep(.swiper-wrapper) {
  overflow: visible;
}

.hover-slide {
  width: var(--slot);
  transition: width 0.25s ease, margin-left 0.25s ease;
  flex-shrink: 0;
  position: relative;
  z-index: 0;
}

/* Grow on hover (only where hover exists) */
@media (hover: hover) and (pointer: fine) {
  .hover-slide:hover {
    width: calc(var(--slot) * 2);
    z-index: 2;
  }
  /* If it's the last in its row, pull it left by one slot so it grows inward */
  .end-item:hover {
    margin-left: calc(var(--slot) * -1.1);
  }
}

/* -------------------- */
/* Breakpoints          */
/* -------------------- */

/* Tablet: 3 items per row, grow to 2 slots, shift = -1 slot */
@media (max-width: 1023.98px) {
  .hover-swiper {
    --slot: 32.3333%;
  }
}

/* Phone: ~1.24 items visible => slot = 100 / 1.24
   Disable hover grow on touch (no hover media already covers most phones) */
@media (max-width: 639.98px) {
  .hover-swiper {
    --slot: 80.6452%;
  } /* 100 / 1.24 */

  /* Just in case a device reports hover, neutralize enlargement on small screens */
  .hover-slide:hover {
    width: var(--slot);
    margin-left: 0;
  }
}
</style>
